import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Input, Checkbox } from "antd";
import { loginUser } from "../store/slice/auth";
import AppNotificationContainer from "../components/controls/AppNotificationContainer";
import { HiPhone, HiLockClosed } from "react-icons/hi";
import {
  FaFaucet,
  FaBolt,
  FaHammer,
  FaShieldAlt,
  FaLock,
} from "react-icons/fa";
import { GiWashingMachine } from "react-icons/gi";

/* ─── service tiles ─────────────────────────────────────────── */
const SERVICES = [
  { icon: <FaFaucet className="text-orange-500 text-lg" />, label: "Plumbing" },
  { icon: <FaBolt className="text-orange-500 text-lg" />, label: "Electrical" },
  {
    icon: <FaHammer className="text-orange-500 text-lg" />,
    label: "Carpentry",
  },
  {
    icon: <GiWashingMachine className="text-orange-500 text-xl" />,
    label: "Appliance",
  },
];

/* ─── trust badges ───────────────────────────────────────────── */
const BADGES = [
  {
    icon: <FaShieldAlt className="text-green-500 text-xl" />,
    line1: "Verified",
    line2: "Professionals",
  },
  {
    icon: <FaShieldAlt className="text-green-500 text-xl" />,
    line1: "Quality",
    line2: "Assurance",
  },
  {
    icon: <FaLock className="text-green-500 text-xl" />,
    line1: "Secure",
    line2: "& Safe",
  },
];

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [mascotError, setMascotError] = useState(false);
  const { isLoggedIn, isLoading, message, error } = useSelector(
    (state) => state.auth,
  );

  useEffect(() => {
    if (isLoggedIn) navigate("/dashboard");
  }, [isLoggedIn]);

  const initialValues = { phoneNumber: "", password: "" };

  const validationSchema = Yup.object({
    phoneNumber: Yup.string()
      .required("Phone number is required")
      .matches(/^[0-9]{10}$/, "Phone number must be 10 digits"),
    password: Yup.string().required("Required"),
  });

  const onSubmit = (values) => dispatch(loginUser(values));

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ background: "#F5F5F5" }}
    >
      <div className="w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden bg-white">
        {/* ══════════════════════════════════════════
            HEADER  – peach banner with mascot
        ══════════════════════════════════════════ */}
        <div
          className="relative"
          style={{
            background: "linear-gradient(170deg, #FDECD8 0%, #FBDAB8 100%)",
          }}
        >
          {/* city-skyline ghost layer (CSS-only) */}
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 120'%3E%3Crect x='10' y='60' width='30' height='60' fill='%23c87941'/%3E%3Crect x='50' y='40' width='40' height='80' fill='%23c87941'/%3E%3Crect x='100' y='50' width='25' height='70' fill='%23c87941'/%3E%3Crect x='280' y='30' width='50' height='90' fill='%23c87941'/%3E%3Crect x='340' y='50' width='30' height='70' fill='%23c87941'/%3E%3C/svg%3E")`,
              backgroundSize: "cover",
              backgroundPosition: "bottom",
            }}
          />

          <div className="relative flex items-end gap-3 px-4 pt-5">
            {/* ── Mascot ── */}
            <div className="w-36 h-36 flex-shrink-0 drop-shadow-md flex items-end justify-center">
              {mascotError ? (
                <span style={{ fontSize: 80, lineHeight: 1 }}>🐘</span>
              ) : (
                <img
                  src="/assets/images/mascot.png"
                  alt="Mr. Hathi"
                  className="w-full h-full object-contain object-bottom"
                  onError={() => setMascotError(true)}
                />
              )}
            </div>

            {/* ── Right: logo + tagline + services ── */}
            <div className="flex-1 pb-5">
              {/* Logo */}
              <div className="flex items-center gap-2 mb-1">
                <div
                  className="rounded-lg p-1.5 flex items-center justify-center"
                  style={{ background: "#1E3A5F" }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M2 12 L12 3 L22 12 V21 H16 V15 H8 V21 H2 Z"
                      fill="white"
                    />
                    <circle cx="15" cy="10.5" r="2.5" fill="#F97316" />
                    <line
                      x1="14"
                      y1="11.5"
                      x2="17"
                      y2="14.5"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                </div>
                <span
                  className="font-extrabold text-2xl tracking-tight"
                  style={{ color: "#1E3A5F" }}
                >
                  fix<span className="text-orange-500">ilo</span>
                </span>
              </div>

              {/* Tagline */}
              <div className="flex items-center gap-1.5 mb-3">
                <span className="block h-px w-5 bg-orange-400" />
                <span className="text-gray-600 text-[11px] whitespace-nowrap">
                  Har Service, Ghar Pe
                </span>
                <span className="block h-px w-5 bg-orange-400" />
              </div>

              {/* Service tiles */}
              <div className="grid grid-cols-4 gap-1.5">
                {SERVICES.map((svc) => (
                  <div
                    key={svc.label}
                    className="flex flex-col items-center bg-white rounded-xl py-1.5 px-1 shadow-sm"
                  >
                    {svc.icon}
                    <span className="text-[9px] text-gray-500 mt-0.5 text-center leading-tight">
                      {svc.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Wave transition to white */}
          <svg
            viewBox="0 0 400 36"
            className="w-full block"
            preserveAspectRatio="none"
            style={{ height: 32, marginBottom: -1 }}
          >
            <path d="M0,36 Q200,0 400,36 L400,36 L0,36 Z" fill="white" />
          </svg>
        </div>

        {/* ══════════════════════════════════════════
            FORM SECTION
        ══════════════════════════════════════════ */}
        <div className="bg-white px-6 pt-2 pb-6">
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={onSubmit}
          >
            {(formik) => (
              <Form noValidate>
                {/* Heading */}
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-extrabold text-gray-900 leading-tight">
                    Sign In
                  </h2>
                  <span className="inline-block w-10 h-1 rounded-full bg-orange-500 mt-1" />
                </div>

                {/* Phone Number */}
                <div className="mb-4">
                  <Field name="phoneNumber">
                    {({ field, form }) => (
                      <Input
                        {...field}
                        type="tel"
                        placeholder="Phone Number"
                        size="large"
                        status={
                          form.errors.phoneNumber && form.touched.phoneNumber
                            ? "error"
                            : ""
                        }
                        prefix={
                          <HiPhone className="text-orange-500 text-lg mr-1" />
                        }
                        className="!rounded-xl"
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="phoneNumber"
                    render={(msg) => (
                      <p className="text-red-500 text-xs mt-1 pl-1">{msg}</p>
                    )}
                  />
                </div>

                {/* Password */}
                <div className="mb-4">
                  <Field name="password">
                    {({ field, form }) => (
                      <Input.Password
                        {...field}
                        placeholder="Password"
                        size="large"
                        status={
                          form.errors.password && form.touched.password
                            ? "error"
                            : ""
                        }
                        prefix={
                          <HiLockClosed className="text-orange-500 text-lg mr-1" />
                        }
                        className="!rounded-xl"
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name="password"
                    render={(msg) => (
                      <p className="text-red-500 text-xs mt-1 pl-1">{msg}</p>
                    )}
                  />
                </div>

                {/* Remember me + Forgot password */}
                <div className="flex justify-between items-center mb-5">
                  <Checkbox className="text-gray-700 text-sm select-none">
                    Remember me
                  </Checkbox>
                  <Link
                    to="/reset-password"
                    className="text-orange-500 hover:text-orange-600 text-sm font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={!formik.isValid || isLoading}
                  className="w-full py-3 rounded-xl font-bold text-sm tracking-widest uppercase text-white transition-all
                             bg-orange-500 hover:bg-orange-600 active:scale-[0.98]
                             disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Signing in…" : "Sign In"}
                </button>

                {/* Register link */}
                <p className="text-center text-gray-600 text-sm mt-5">
                  Not a member?{" "}
                  <Link
                    to="/register"
                    className="text-orange-500 hover:text-orange-600 font-semibold transition-colors"
                  >
                    Register
                  </Link>
                </p>
              </Form>
            )}
          </Formik>
        </div>

        {/* ══════════════════════════════════════════
            TRUST BADGES
        ══════════════════════════════════════════ */}
        <div className="flex items-center justify-around px-4 py-3 border-t border-gray-100 bg-white">
          {BADGES.map((badge, i) => (
            <React.Fragment key={badge.line1}>
              <div className="flex flex-col items-center gap-1">
                <div className="w-9 h-9 rounded-full bg-green-50 flex items-center justify-center">
                  {badge.icon}
                </div>
                <p className="text-[10px] text-gray-500 text-center leading-tight">
                  {badge.line1}
                  <br />
                  {badge.line2}
                </p>
              </div>
              {i < BADGES.length - 1 && (
                <span className="block w-px h-10 bg-gray-200" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <AppNotificationContainer
        loading={isLoading}
        msg={message}
        error={error}
      />
    </div>
  );
}

export default Login;
